// Joana chat shell — the state store. Holds the whole shell state as one
// immutable value and notifies a subscriber on every change. The surface
// re-renders by building a fresh catalog tree from the current state (a pure
// function of state, like the mini-app render() model — the trusted shell just
// builds trees directly instead of through an isolate).
//
// Mutations are small, named, and replace state immutably so the diff engine
// sees clean before/after trees. No view logic lives here.

import { LANGUAGE_KEY, decodeLanguage } from "../i18n/index.ts";
import type { ThemeChoice } from "./appearance.ts";
import { DEFAULT_WALLPAPER, type ChatWallpaper } from "./wallpaper.ts";
import type {
  ActivityEvent,
  Channel,
  ChannelListing,
  ChannelPost,
  ReactionCount,
  Chat,
  CirclePost,
  PostComment,
  Message,
  MiniAppListItem,
  PrivacyAudience,
  AssistantSetup,
  OrgApiKey,
  Organisation,
  OrganisationMember,
  Publisher,
  StatusItem,
  WirePerson,
  StatusTrayEntry,
  User,
} from "./types.ts";

/**
 * The one open editor on an organisation's screen (ADR-0037 E): naming the
 * assistant, a zone's assistant, issuing a key, the key shown ONCE, or a revoke
 * awaiting its confirmation. One slot, so two can never be open together —
 * and closing it is what drops an issued key's secret from this tab.
 */
export type OrgEditor =
  | { kind: "assistant_name"; creating: boolean; value: string }
  | { kind: "zone_assistant"; chat_id: string; zone_name: string; enabled: boolean; shape: string; instructions: string }
  | { kind: "key_issue"; name: string; read: boolean; post: boolean; days: number }
  | { kind: "key_issued"; secret: string; copied: boolean }
  | { kind: "key_revoke"; key_id: string; name: string };

export type Screen =
  | "login"
  | "chats"
  | "conversation"
  | "apps"
  | "profile"
  | "updates"
  | "explore"
  // ADR-0024 D6/D7, Phase 3 — Office. Three screens because they are three
  // steps of one job: what do I run, make one, and publish from it.
  | "office"
  | "office_new"
  | "office_publisher"
  // ADR-0018 step 1 — organisations and zones. Four screens, and the ZONE is
  // not one of them: a zone IS a chat, so it opens `conversation` like any
  // other. A parallel zone screen would be a second message surface to keep in
  // step with the first.
  | "office_orgs"
  | "office_org"
  | "office_org_new"
  | "office_zone_new";
export type Connection = "offline" | "connecting" | "online";
export type MiniAppStatus = "idle" | "loading" | "running" | "error";
/** Picker tabs: featured registry apps vs the user's installed apps. */
export type AppsTab = "featured" | "installed";

/** How the login screen authenticates: scan-with-phone (default, the WeChat
 *  model) or the classic username/password fallback. */
export type LoginMode = "qr" | "password";
export type QrStatus = "loading" | "waiting" | "scanned" | "expired" | "rejected" | "error";

/** The QR login panel's reflected state. `svg` is the server-rendered QR
 *  (data-URI-ready markup); `scannedBy` is the display name shown during the
 *  confirm-on-phone step. */
export interface QrLoginState {
  status: QrStatus;
  svg: string | null;
  scannedBy: string | null;
}

/** The embedded mini-app overlay's reflected state (the overlay DOM itself is
 *  owned by the MiniAppHost; this is what the shell shows about it). */
export interface MiniAppUiState {
  open: boolean;
  title: string;
  status: MiniAppStatus;
  error: string | null;
}

export interface ShellState {
  screen: Screen;
  connection: Connection;
  /**
   * ADR-0033 D9 — the server refuses this BUNDLE (`426`), and nothing on the
   * page will work until it is replaced.
   *
   * A latch: never lowered, because the only thing that changes the answer is
   * loading a newer bundle, and a flag that cleared on the next successful
   * request would flicker the wall on and off as requests raced.
   */
  updateRequired: boolean;
  me: User | null;

  /**
   * A stored session is being exchanged for a live one, right now.
   *
   * Drawn instead of the sign-in form so a reload does not flash a login screen
   * at somebody who is already signed in — which is what the whole restore path
   * exists to stop. Never a latch: it is lowered on both outcomes.
   */
  restoringSession: boolean;

  // login form
  loginMode: LoginMode;
  qr: QrLoginState;
  /** ADR-0027 D4.4 — is this browser a Signal DEVICE, not merely signed in?
   *  `null` before a login has answered the question. */
  signalDeviceLinked: boolean | null;
  /**
   * ADR-0027 D4.7 — how far through a history bundle a phone is.
   *
   * `received` is the highest frame number seen, not a count of frames: a
   * redelivered envelope repeats a number already passed and must not move the
   * bar.
   *
   * Null is the ordinary state, and it means one of two very different things
   * on purpose: nothing is syncing, or the last bundle finished. Neither is
   * worth a sentence — what a person needs told is that their chats are still
   * arriving, and only while that is true.
   */
  historySync: { received: number; total: number } | null;
  loginUsername: string;
  loginPassword: string;
  twoFactorToken: string | null;
  twoFactorCode: string;

  // data
  chats: Chat[];
  activeChatId: string | null;
  /**
   * Is the OPEN chat end-to-end encrypted? (ADR-0007.)
   *
   * Three states, and the third is load-bearing: `true`, `false`, and `null`
   * for "not answered yet". A screen that defaults to `false` while the
   * request is in flight tells everybody their conversation is readable for
   * as long as the network takes, including the people for whom that is
   * false — so nothing is drawn at all until the server has said.
   */
  activeChatE2E: boolean | null;
  /**
   * ADR-0037 B — the open chat is a zone whose minimum web build is above this
   * bundle, so the server refused its history and its channel. The detail pane
   * draws "update to open this zone" instead of the thread and the composer.
   * False until the server has said so.
   */
  activeZoneUpdateRequired: boolean;
  /** Active chat's messages in DISPLAY order (oldest → newest). */
  messages: Message[];
  /** s175: authors of the active chat the row's rollup (bounded at 8) cannot
   *  name, from the full roster — see author-names.ts. Cleared on every open. */
  rosterNames: Record<string, string>;
  draft: string;
  /** user_ids currently typing in the active chat. */
  typing: string[];

  // ui
  loading: boolean;
  error: string | null;

  // embedded mini-app overlay
  miniApp: MiniAppUiState;

  // profile (ADR-0022)
  profile: User | null;
  profileBusy: boolean;
  profileError: string | null;
  /** E5 — this account's own contact QR, rendered SERVER-side (a browser is
   *  online by definition; the phones draw their own — ADR-0032 D3). Null
   *  until Profile is opened and the request returns. */
  contactQr: { payload: string; svg: string | null } | null;
  /** E5 / B9 — the card a tapped contact bubble opened, or null. `notFound`
   *  covers three server answers that are deliberately the same one: nobody,
   *  inactive, and hidden. */
  contactCard: {
    userId: string;
    loading: boolean;
    notFound: boolean;
    user: { id: string; username: string | null; display_name: string | null; reachability?: string | null } | null;
  } | null;
  /**
   * ADR-0007 — the safety-number screen, or null when it is closed.
   *
   * `digits` is null while `loading`, and null AFTER it with `loading` false
   * meaning "there is nothing to compare yet" — the ordinary state for a
   * browser that has not received an encrypted message from this person, and
   * NOT an alarm. `peerDevice` names which of the peer's devices the number
   * was computed against, because a fingerprint is between two devices and a
   * screen that hid that would be claiming the number belongs to the account.
   */
  safetyNumber: {
    chatId: string;
    /** The person, as the conversation names them — for the sentence. */
    peerLabel: string;
    loading: boolean;
    digits: string | null;
    peerDevice: number | null;
  } | null;
  /** ADR-0028 — the chosen interface language, or null for "follow my
   *  browser". The ACCOUNT owns it; this is the copy that lets the first
   *  paint be right instead of English-until-a-request-returns. */
  uiLanguage: string | null;
  privacy: { policy: string; except: { user_id: string; username: string | null; display_name: string | null }[] } | null;
  /** ADR-0031 D3 — "Who can find me by my phone number". A whole
   *  `PrivacyAudience` rather than a bare policy: the SERVER says which
   *  policies this surface offers and the section renders that list, so the
   *  web cannot come to disagree with the phones about what the setting is.
   *  Null until Profile has fetched it — the section draws nothing then,
   *  rather than a row that claims a default nobody has confirmed. */
  findability: PrivacyAudience | null;
  history: { version: string; set_at: string | null; removed_at: string | null; current: boolean }[];
  /** Bumped whenever an avatar blob lands, so the pure view rebuilds. */
  avatarTick: number;

  /**
   * The window's width in CSS pixels — the three-pane frame's one input.
   *
   * In the STATE rather than read from `window` inside a view, because a view
   * here is a pure function of state and a layout that reached for a global
   * would be untestable and would not redraw when the window changed. The
   * browser glue sets it before the first paint and on every resize; Node
   * tests set it directly, which is how the three breakpoints are asserted
   * without a browser.
   *
   * The default is a desktop, not a phone: this is a browser client, and a
   * narrow default would make every non-browser caller (tests, the verify
   * harness before its first resize) assert the one shape the frame exists to
   * replace.
   */
  viewportWidth: number;

  /** Light, dark, or follow the machine. Per DEVICE, like the phones — see
   *  `appearance.ts` for why it is not an account setting. */
  appearance: ThemeChoice;
  /** G16 — what the conversation is drawn ON. Per BROWSER, like the
   *  appearance and for the shared module's reason; see `wallpaper.ts`. */
  wallpaper: ChatWallpaper;

  // status (D8, ADR-0023)
  /** The ONE tray. Every surface that shows a status reads this. */
  statusTray: StatusTrayEntry[];
  /** This user's own live statuses, with the count only the author sees. */
  statusOwn: StatusItem[];
  /** Whose status is open in the viewer, and where in their items. */
  statusViewing: string | null;
  /** The status the report sheet is about, or null when it is closed. */
  statusReporting: string | null;
  /** A confirmation shown OVER the viewer. Distinct from `statusError`, which
   *  is red and lives on the Status screen — a success message in an error
   *  field renders as a failure on a screen the reader is not looking at. */
  statusNotice: string | null;
  statusIndex: number;
  statusAudience: PrivacyAudience | null;
  /** The composer's draft. `kind` decides which of the rest is meaningful. */
  statusDraft: { kind: "text" | "mood"; body: string; background: string; moodId: string };
  statusBusy: boolean;
  statusError: string | null;
  /** Bumped whenever a status blob lands, so the pure view rebuilds. */
  statusTick: number;

  // circle posts (D9, ADR-0024)
  /** The ONE feed. Reverse-chronological, no ranking. */
  feed: CirclePost[];
  /** The post whose comment box is open, or null. Only one at a time: a feed
   *  with six open comment boxes is a feed nobody can read. */
  feedCommenting: string | null;
  feedDraft: string;
  /** The post composer's draft, and the audience chosen FOR IT — a post picks
   *  per post, unlike a status, which reads a standing setting (A2). */
  postDraft: { body: string; audience: string };
  postBusy: boolean;
  feedError: string | null;

  // channels (D6, ADR-0024)
  /** Which world the Updates screen is showing. Horizontal choice; A13's
   *  expand is the vertical one, and the two never overlap. */
  updatesSegment: "circle" | "world";
  /** Channels this viewer follows — the ONE copy, like the tray and the feed. */
  channels: Channel[];
  channelSuggestions: Channel[];
  /**
   * Explore's page — a QUERY, not an account fact.
   *
   * It belongs to the kind and the words somebody just typed, which is why it
   * is reset when the screen opens rather than kept: a directory that comes
   * back showing the last search is one that answers a question nobody asked.
   */
  exploreResults: ChannelListing[];
  exploreQuery: string;
  exploreKind: string | null;
  exploreBusy: boolean;
  exploreAtEnd: boolean;
  exploreError: string | null;
  /** Whether Explore should open with the search box focused. */
  exploreFocus: boolean;

  // --- Office (ADR-0024 D6/D7, Phase 3) ---
  /** The publishers this user owns. */
  publishers: Publisher[];
  /** Has the list landed at least once? Distinct from "is it empty": those two
   *  want opposite screens, and an explanation shown mid-flight is one that may
   *  be about to be wrong. */
  publishersLoaded: boolean;
  /** Which publisher the detail screen is showing. */
  officePublisherId: string | null;
  /** Which screen Office was opened FROM — see `openOffice`. */
  officeReturn: Screen;
  officeTimeline: ChannelPost[];
  officeDraft: string;
  officeBusy: boolean;
  officeError: string | null;
  /** The create form. Held in the store rather than in the view, because
   *  Joana's views are pure functions of state — a form that kept its own value
   *  would lose it on every re-render. */
  /** ADR-0018 — the organisations I am in. */
  organisations: Organisation[];
  organisationsLoaded: boolean;
  /** Which organisation the detail screen is showing. */
  activeOrgId: string | null;
  /**
   * That organisation's zones (chat rows) and its roster.
   *
   * The zones are CHAT rows, because a zone is a chat: one row type, one
   * renderer, one preview. A parallel zone shape would be a second copy of the
   * same conversation that disagrees the first time a message arrives on one
   * path and not the other.
   */
  orgZones: Chat[];
  orgMembers: OrganisationMember[];
  /**
   * Zone rows fetched on their own (s185): a zone the chat LIST never carries
   * and the Office screen has not loaded — one just created, opened straight
   * into its thread — whose header would otherwise say "New conversation".
   * Kept apart from `chats`, because `chats` IS the Chats list and a zone is
   * never in Chats.
   */
  zoneRows: Record<string, Chat>;
  orgBusy: boolean;
  orgError: string | null;
  /** ADR-0037 E — the managers' view of the open organisation's assistant and
   *  its API keys. NULL is "not read" (or not a manager), never "none". */
  assistantSetup: AssistantSetup | null;
  orgApiKeys: OrgApiKey[] | null;
  orgEditor: OrgEditor | null;
  /** The create forms. Cleared on OPEN — a form that comes back holding the
   *  last attempt is one somebody submits by accident. */
  orgForm: { name: string };
  zoneForm: { name: string; kind: string };
  officeForm: {
    name: string;
    displayName: string;
    description: string;
    kind: string | null;
    category: string | null;
    email: string;
    phone: string;
  };
  /** The industries a BUSINESS may trade in, from the server (A15). */
  businessCategories: string[];

  // --- Mazungumzo (D12, ADR-0025) ---
  /** Which of Duniani's two sections is showing. A10: they are not one scroll,
   *  because the reply model differs and it must not change mid-scroll. */
  duniamiTab: "channels" | "mazungumzo";
  /** The ranked front door. The order is the SERVER's; the shell never re-sorts,
   *  and no score arrives to re-sort by (D8). */
  mazungumzoRanked: CirclePost[];
  /** Chronological, and empty until this viewer follows somebody. Empty is a
   *  real answer and never falls back to the ranked feed (A11). */
  mazungumzoFollowing: CirclePost[];
  /** Which segment is showing. The CONTROL only exists once `followed` is
   *  non-empty, so most viewers see one feed and no switcher at all. */
  mazungumzoShowFollowing: boolean;
  /** Who this viewer follows. There is no inverse and no count (D2). */
  followed: WirePerson[];
  /** Has the follow list landed? "You follow nobody" and "not asked yet" want
   *  opposite words on screen. */
  mazungumzoLoaded: boolean;
  /** ADR-0029 D17 — the Feeds drawer's lists, and the state each one needs.
   *  Held here rather than in the view so a redraw cannot lose which list is
   *  open, which is the bug a component-local flag always eventually is. */
  mazungumzoSaved: CirclePost[];
  mazungumzoLiked: CirclePost[];
  mazungumzoActivity: ActivityEvent[];
  mazungumzoActivityFilter: string | null;
  mazungumzoFeedList: "saved" | "liked" | "activity" | null;
  mazungumzoThread: CirclePost[];
  mazungumzoQuoting: string | null;
  mazungumzoQuery: string;
  mazungumzoSearchPosts: CirclePost[];
  mazungumzoSearchPeople: WirePerson[];
  /** The publisher kinds as the SERVER defines them. Empty until it answers:
   *  nothing to browse is honest, a stale list is confidently wrong. */
  publisherKinds: string[];
  /** The channel whose timeline is open, or null. */
  channelViewing: string | null;
  /** The header's facts (ADR-0036), from `GET /channels/:id` — null until it
   *  answers, and the list row's name stands in meanwhile. */
  channelDetail: Channel | null;
  /** Newest FIRST, as the wire serves them; the view reverses. */
  channelPosts: ChannelPost[];
  channelBusy: boolean;
  channelError: string | null;
  /** Paging: the server answered a short page, or is being asked for more. */
  channelAtEnd: boolean;
  channelLoadingMore: boolean;
  /** Posts whose clip or voice note the reader asked to play. Nothing is
   *  fetched before that — the size pill is the promise, and a poster that
   *  quietly pulled the clip would break it (ADR-0023 D11). */
  channelPlaying: string[];
  /** The photo open full-screen, or null. */
  channelLightbox: string | null;
  /** The post being forwarded — the chat picker is open — or null. */
  channelForwarding: string | null;
  /** The post whose emoji row is open, or null. */
  channelPicker: string | null;
  /** One line under the thread: "Forwarded." / "Couldn't forward." */
  channelNotice: string | null;

  // mini-app picker
  apps: MiniAppListItem[];
  appsLoading: boolean;
  appsError: string | null;
  appsTab: AppsTab;
  appsQuery: string;
}

const INITIAL: ShellState = {
  profile: null,
  profileBusy: false,
  profileError: null,
  uiLanguage: null,
  privacy: null,
  findability: null,
  history: [],
  avatarTick: 0,
  activeChatE2E: null,
  activeZoneUpdateRequired: false,
  viewportWidth: 1280,
  appearance: "system",
  wallpaper: DEFAULT_WALLPAPER,
  screen: "login",
  connection: "offline",
  updateRequired: false,
  me: null,
  restoringSession: false,
  loginMode: "qr",
  qr: { status: "loading", svg: null, scannedBy: null },
  signalDeviceLinked: null,
  historySync: null,
  contactQr: null,
  contactCard: null,
  safetyNumber: null,
  loginUsername: "",
  loginPassword: "",
  twoFactorToken: null,
  twoFactorCode: "",
  chats: [],
  activeChatId: null,
  messages: [],
  rosterNames: {},
  draft: "",
  typing: [],
  loading: false,
  error: null,
  miniApp: { open: false, title: "", status: "idle", error: null },
  apps: [],
  appsLoading: false,
  appsError: null,
  appsTab: "featured",
  appsQuery: "",
  statusTray: [],
  statusOwn: [],
  statusViewing: null,
  statusReporting: null,
  statusNotice: null,
  statusIndex: 0,
  statusAudience: null,
  statusDraft: { kind: "text", body: "", background: "#FF8A3D", moodId: "" },
  statusBusy: false,
  feed: [],
  feedCommenting: null,
  feedDraft: "",
  postDraft: { body: "", audience: "contacts" },
  postBusy: false,
  feedError: null,
  updatesSegment: "circle",
  channels: [],
  channelSuggestions: [],
  exploreResults: [],
  exploreQuery: "",
  exploreKind: null,
  exploreBusy: false,
  exploreAtEnd: false,
  exploreError: null,
  exploreFocus: false,

  publishers: [],
  publishersLoaded: false,
  officePublisherId: null,
  officeReturn: "profile",
  officeTimeline: [],
  officeDraft: "",
  officeBusy: false,
  officeError: null,
  organisations: [],
  organisationsLoaded: false,
  activeOrgId: null,
  orgZones: [],
  orgMembers: [],
  zoneRows: {},
  orgBusy: false,
  orgError: null,
  assistantSetup: null,
  orgApiKeys: null,
  orgEditor: null,
  orgForm: { name: "" },
  zoneForm: { name: "", kind: "general" },
  officeForm: {
    name: "",
    displayName: "",
    description: "",
    kind: null,
    category: null,
    email: "",
    phone: "",
  },
  businessCategories: [],

  duniamiTab: "channels",
  mazungumzoRanked: [],
  mazungumzoFollowing: [],
  mazungumzoShowFollowing: false,
  followed: [],
  mazungumzoLoaded: false,
  mazungumzoSaved: [],
  mazungumzoLiked: [],
  mazungumzoActivity: [],
  mazungumzoActivityFilter: null,
  mazungumzoFeedList: null,
  mazungumzoThread: [],
  mazungumzoQuoting: null,
  mazungumzoQuery: "",
  mazungumzoSearchPosts: [],
  mazungumzoSearchPeople: [],
  publisherKinds: [],
  channelViewing: null,
  channelDetail: null,
  channelPosts: [],
  channelBusy: false,
  channelError: null,
  channelAtEnd: false,
  channelLoadingMore: false,
  channelPlaying: [],
  channelLightbox: null,
  channelForwarding: null,
  channelPicker: null,
  channelNotice: null,
  statusError: null,
  statusTick: 0,
};

export class ShellStore {
  private current: ShellState;
  private subscriber: ((s: ShellState) => void) | null = null;

  constructor(initial: Partial<ShellState> = {}) {
    this.current = { ...INITIAL, ...initial };
  }

  get state(): ShellState {
    return this.current;
  }

  /** Register the (single) render subscriber. Returns an unsubscribe fn. */
  subscribe(fn: (s: ShellState) => void): () => void {
    this.subscriber = fn;
    return () => {
      if (this.subscriber === fn) this.subscriber = null;
    };
  }

  // The one mutator. Everything in this class goes through it.
  //
  // It used to have a phantom twin: eight call sites said `this.update(...)`,
  // which was never defined — the profile actions, including `openProfile`,
  // every one of them reachable from the profile screen. Nothing caught it
  // because the test runner strips types rather than checking them
  // (`node --experimental-transform-types`), so a call to a method that does
  // not exist is a runtime TypeError and nothing else. Opening Profile on the
  // web threw, which is why the owed "drive the web profile screen in Chrome"
  // pass had never passed. Fixed in s130 while adding Status; the guard against
  // it recurring is `store.test.ts`, which now CALLS every mutator rather than
  // trusting that a method named in this file exists.
  private set(patch: Partial<ShellState>): void {
    this.current = { ...this.current, ...patch };
    this.subscriber?.(this.current);
  }

  // --- login ---------------------------------------------------------------

  setLoginField(field: "loginUsername" | "loginPassword" | "twoFactorCode", value: string): void {
    this.set({ [field]: value } as Partial<ShellState>);
  }

  /** Switch between scan-with-phone and password sign-in. */
  setLoginMode(mode: LoginMode): void {
    this.set({ loginMode: mode, error: null });
  }

  /** E5 — the account's own contact code, once the server has rendered it. */
  setContactQr(payload: string, svg: string | null): void {
    this.set({ contactQr: { payload, svg } });
  }

  /** E5 / B9 — open, resolve or close the contact card. */
  setContactCard(card: ShellState["contactCard"]): void {
    this.set({ contactCard: card });
  }

  /** ADR-0007 — open, update or close the safety-number screen. */
  setSafetyNumber(v: ShellState["safetyNumber"]): void {
    this.set({ safetyNumber: v });
  }

  setQr(qr: Partial<QrLoginState>): void {
    this.set({ qr: { ...this.current.qr, ...qr } });
  }

  /**
   * Whether this browser is a Signal DEVICE (ADR-0027 D4.4), as opposed to
   * merely being signed in.
   *
   * `false` is a real state with a real consequence — an encrypted chat will
   * not open here — and it is held so the UI can SAY that. The whole s150
   * disaster was a surface asserting an encryption state it could not deliver;
   * the inverse mistake is a surface staying silent about one it cannot.
   */
  setSignalDeviceLinked(linked: boolean): void {
    this.set({ signalDeviceLinked: linked });
  }

  /**
   * ADR-0027 D4.7 — a history frame arrived; this is where it got to.
   *
   * MAX, never a running count. Frames arrive in order and an unacked one comes
   * round again on the next drain, so counting would double past what the phone
   * has actually sent and a late redelivery would appear to move the bundle
   * backwards. A number that only ever goes up is the honest one.
   *
   * A frame belonging to a NEW bundle — a different total — replaces the state
   * rather than continuing it: a second browser-link is a second bundle, and
   * carrying the old progress into it would show 900 of 40.
   */
  setHistorySync(seq: number, total: number): void {
    // The last frame of the bundle ends the state rather than filling the bar.
    // "100%" is a thing to look at, not a thing to say.
    if (total <= 0 || seq >= total) {
      this.clearHistorySync();
      return;
    }
    const current = this.state.historySync;
    const received =
      current && current.total === total ? Math.max(current.received, seq) : seq;
    this.set({ historySync: { received, total } });
  }

  /**
   * Stop saying it.
   *
   * Called when the bundle finishes and when it STALLS — a phone that goes
   * offline half way through leaves a bundle nothing will finish, and a bar
   * that sits at 46% forever is a worse lie than saying nothing. When the
   * phone comes back the next frame puts it up again.
   */
  clearHistorySync(): void {
    if (this.state.historySync !== null) this.set({ historySync: null });
  }

  setLoading(loading: boolean): void {
    this.set({ loading });
  }

  setError(error: string | null): void {
    this.set({ error, loading: false });
  }

  needTwoFactor(challengeToken: string): void {
    this.set({ twoFactorToken: challengeToken, loading: false, error: null });
  }

  loginSuccess(me: User): void {
    this.set({
      me,
      screen: "chats",
      twoFactorToken: null,
      twoFactorCode: "",
      loginPassword: "",
      loading: false,
      error: null,
    });
    // ADR-0028 D3 — the ACCOUNT owns the interface language, so signing in has
    // to adopt it. Until s148 only `openProfile` did: somebody who set
    // Kiswahili on their phone signed in here and got English until they
    // happened to open Profile, which is the one screen they had no reason to
    // visit. The drive found it — every gate was green, and the s147 rule that
    // a ratchet at zero is not one language on screen had just been written.
    this.adoptAccountLanguage(me);
  }

  setConnection(connection: Connection): void {
    this.set({ connection });
  }

  /** ADR-0033 D9 — raise the wall. One-way: see `ShellState.updateRequired`. */
  setUpdateRequired(): void {
    if (!this.state.updateRequired) this.set({ updateRequired: true });
  }

  setAppearance(appearance: ThemeChoice): void {
    this.set({ appearance });
  }

  /** G16 — the chosen chat wallpaper. The COLOURS reach the page as CSS (see
   *  `wallpaperCss`); this is what the picker draws its tick against. */
  setWallpaper(wallpaper: ChatWallpaper): void {
    this.set({ wallpaper });
  }

  /** Record the window width (the frame's breakpoint input). A no-op when it
   *  has not changed, so a resize drag is not a render storm. */
  setViewportWidth(width: number): void {
    if (!Number.isFinite(width) || width <= 0) return;
    if (this.current.viewportWidth === width) return;
    this.set({ viewportWidth: width });
  }

  /** A stored refresh token is being exchanged for a session. */
  setRestoringSession(restoring: boolean): void {
    this.set({ restoringSession: restoring });
  }

  /**
   * The restore could not be completed and we do not know why — offline, or the
   * server having a bad moment.
   *
   * Deliberately NOT a sign-out: the stored token is kept, and the next reload
   * on a working network picks the session straight back up. All this does is
   * stop the spinner, say so, and leave the ordinary sign-in available for
   * somebody who would rather not wait.
   */
  setSessionRestoreFailed(message: string): void {
    this.set({ restoringSession: false, error: message });
  }

  /** Clear the session back to a fresh login screen (logout). */
  loginReset(): void {
    this.set({
      screen: "login",
      restoringSession: false,
      me: null,
      loginMode: "qr",
      qr: { status: "loading", svg: null, scannedBy: null },
      contactQr: null,
      contactCard: null,
      safetyNumber: null,
      loginUsername: "",
      loginPassword: "",
      twoFactorToken: null,
      twoFactorCode: "",
      activeChatId: null,
      messages: [],
      rosterNames: {},
      draft: "",
      typing: [],
      loading: false,
      error: null,
    });
  }

  // --- chat list -----------------------------------------------------------

  openProfile(): void {
    this.set({ screen: "profile", profileError: null });
  }

  // --- Status (D8, ADR-0023) ------------------------------------------------

  openStatus(): void {
    this.set({ screen: "updates", statusError: null });
  }

  /** The tray has ONE home. Every surface reads it; nothing keeps a copy. */
  setStatusTray(tray: StatusTrayEntry[]): void {
    this.set({ statusTray: tray });
  }

  setStatusOwn(own: StatusItem[]): void {
    this.set({ statusOwn: own });
  }

  setStatusAudience(audience: PrivacyAudience | null): void {
    this.set({ statusAudience: audience });
  }

  /** Open somebody's status at their first unseen item. */
  viewStatus(authorId: string): void {
    const entry = this.state.statusTray.find((e) => e.author_id === authorId);
    const first = entry ? entry.items.findIndex((i) => !i.viewed) : 0;
    this.set({ statusViewing: authorId, statusIndex: first >= 0 ? first : 0 });
  }

  closeStatus(): void {
    this.set({ statusViewing: null, statusIndex: 0, statusReporting: null, statusNotice: null });
  }

  /** Open or close the report sheet over the viewer. */
  reportStatus(statusId: string | null): void {
    this.set({ statusReporting: statusId });
  }

  setStatusNotice(statusNotice: string | null): void {
    this.set({ statusNotice });
  }

  setStatusIndex(index: number): void {
    this.set({ statusIndex: index });
  }

  setStatusDraft(draft: Partial<ShellState["statusDraft"]>): void {
    this.set({ statusDraft: { ...this.state.statusDraft, ...draft } });
  }

  setStatusBusy(busy: boolean, error: string | null = null): void {
    this.set({ statusBusy: busy, statusError: error });
  }

  // --- Circle posts (D9, ADR-0024) --------------------------------------

  setFeed(feed: CirclePost[]): void {
    this.set({ feed });
  }

  setPostDraft(draft: Partial<ShellState["postDraft"]>): void {
    this.set({ postDraft: { ...this.state.postDraft, ...draft } });
  }

  // --- Channels (D6, ADR-0024) ---------------------------------------------

  setUpdatesSegment(segment: "circle" | "world"): void {
    this.set({ updatesSegment: segment });
  }

  setChannels(channels: Channel[]): void {
    this.set({ channels });
  }

  setChannelSuggestions(channelSuggestions: Channel[]): void {
    this.set({ channelSuggestions });
  }

  /** Open Explore. The page is CLEARED, not kept: a directory that comes back
   *  showing the last search answers a question nobody asked. */
  openExplore(focus: boolean): void {
    this.set({
      screen: "explore",
      exploreResults: [],
      exploreQuery: "",
      exploreKind: null,
      exploreBusy: true,
      exploreAtEnd: false,
      exploreError: null,
      exploreFocus: focus,
    });
  }

  closeExplore(): void {
    this.set({ screen: "updates", exploreFocus: false });
  }

  setExploreQuery(exploreQuery: string): void {
    this.set({ exploreQuery });
  }

  setExploreKind(exploreKind: string | null): void {
    this.set({ exploreKind });
  }

  setExploreBusy(exploreBusy: boolean): void {
    this.set({ exploreBusy });
  }

  setExploreResults(rows: ChannelListing[], opts: { append: boolean; atEnd: boolean }): void {
    this.set({
      exploreResults: opts.append ? [...this.state.exploreResults, ...rows] : rows,
      exploreAtEnd: opts.atEnd,
      exploreBusy: false,
      exploreError: null,
    });
  }

  // --- Mazungumzo (D12, ADR-0025) ---

  setDuniamiTab(duniamiTab: ShellState["duniamiTab"]): void {
    this.set({ duniamiTab });
  }

  setMazungumzoSegment(mazungumzoShowFollowing: boolean): void {
    this.set({ mazungumzoShowFollowing });
  }

  // --- ADR-0029: Threads inside WaveTalk ---

  setSavedPosts(posts: CirclePost[]): void { this.set({ mazungumzoSaved: posts }); }
  setLikedPosts(posts: CirclePost[]): void { this.set({ mazungumzoLiked: posts }); }
  setActivity(events: ActivityEvent[]): void { this.set({ mazungumzoActivity: events }); }
  setActivityFilter(filter: string | null): void { this.set({ mazungumzoActivityFilter: filter }); }
  setFeedList(list: "saved" | "liked" | "activity" | null): void {
    this.set({ mazungumzoFeedList: list });
  }
  setThread(posts: CirclePost[]): void { this.set({ mazungumzoThread: posts }); }
  setQuoting(postId: string | null): void { this.set({ mazungumzoQuoting: postId }); }
  setSearchQuery(q: string): void { this.set({ mazungumzoQuery: q }); }
  setSearchResults(posts: CirclePost[], people: WirePerson[]): void {
    this.set({ mazungumzoSearchPosts: posts, mazungumzoSearchPeople: people });
  }

  setMazungumzo(ranked: CirclePost[], following: CirclePost[], followed: WirePerson[]): void {
    this.set({
      mazungumzoRanked: ranked,
      mazungumzoFollowing: following,
      followed,
      mazungumzoLoaded: true,
    });
  }


  // --- Office (ADR-0024 D6/D7, Phase 3) ---

  /**
   * Open Office, remembering where from.
   *
   * It has two doors — the Profile screen's row and the World segment's
   * "Create a channel" — and back has to return through the one that was used.
   * A fixed destination sends half the people who arrive here somewhere they
   * were not, which reads as the app losing its place. The return is recorded
   * only on the FIRST open, so stepping back out of the publisher screen into
   * the list does not rewrite it.
   */
  openOffice(from?: Screen): void {
    this.set({
      screen: "office",
      officeError: null,
      officeReturn: from ?? this.state.officeReturn,
    });
  }

  closeOffice(): void {
    this.set({ screen: this.state.officeReturn, officeError: null });
  }

  /** The create form is CLEARED on open: a form that comes back holding the
   *  last attempt's handle is one somebody submits by accident. */
  openOfficeCreate(): void {
    this.set({
      screen: "office_new",
      officeError: null,
      officeForm: {
        name: "",
        displayName: "",
        description: "",
        kind: null,
        category: null,
        email: "",
        phone: "",
      },
    });
  }

  openPublisher(officePublisherId: string): void {
    this.set({
      screen: "office_publisher",
      officePublisherId,
      officeTimeline: [],
      officeDraft: "",
      officeError: null,
    });
  }

  // --- ADR-0018 step 1: organisations and zones ----------------------------

  /** Office's second section. Back returns to Office, not to wherever Office
   *  was opened from — the organisation list is one step INSIDE it. */
  openOrganisations(): void {
    this.set({ screen: "office_orgs", orgError: null });
  }

  openOrganisation(activeOrgId: string): void {
    // The zones and roster are CLEARED, not kept: showing the previous
    // organisation's people under this one's name for the length of a fetch is
    // a screen that is briefly, confidently wrong about who works where.
    this.set({
      screen: "office_org",
      activeOrgId,
      orgZones: [],
      orgMembers: [],
      orgError: null,
      assistantSetup: null,
      orgApiKeys: null,
      orgEditor: null,
    });
  }

  /** ADR-0037 E — the managers' read of the assistant and keys. Either may be
   *  null when it could not be read; the screen says so rather than "none". */
  setAssistantData(assistantSetup: AssistantSetup | null, orgApiKeys: OrgApiKey[] | null): void {
    this.set({ assistantSetup, orgApiKeys });
  }

  setOrgEditor(orgEditor: OrgEditor | null): void {
    this.set({ orgEditor, orgError: null });
  }

  /** One field of the open editor, of whatever kind it is. */
  patchOrgEditor(patch: Record<string, unknown>): void {
    const open = this.current.orgEditor;
    if (!open) return;
    this.set({ orgEditor: { ...open, ...patch } as OrgEditor });
  }

  /** Back from a zone: to the organisation it belongs to, in one step. A zone
   *  is never in Chats, so `backToChats` would land somewhere it was not
   *  opened from. The same organisation's zones and roster are KEPT — they are
   *  still true, and clearing them would flash a loading screen for nothing. */
  leaveZone(orgId: string): void {
    const same = this.current.activeOrgId === orgId;
    this.set({
      screen: "office_org", activeOrgId: orgId, orgError: null,
      ...(same ? {} : { orgZones: [], orgMembers: [] }),
      activeChatId: null, messages: [], typing: [],
      activeChatE2E: null, safetyNumber: null, activeZoneUpdateRequired: false,
    });
  }

  openOrganisationCreate(): void {
    this.set({ screen: "office_org_new", orgError: null, orgForm: { name: "" } });
  }

  openZoneCreate(): void {
    this.set({
      screen: "office_zone_new",
      orgError: null,
      zoneForm: { name: "", kind: "general" },
    });
  }

  setOrganisations(organisations: Organisation[]): void {
    this.set({ organisations, organisationsLoaded: true });
  }

  setOrganisationDetail(
    organisation: Organisation,
    orgZones: Chat[],
    orgMembers: OrganisationMember[],
  ): void {
    this.set({
      // The row in the list is replaced by the freshly read one rather than
      // left beside it, so the count on the list and the roster on the detail
      // screen can never be two different answers to the same question.
      organisations: this.state.organisations.some((o) => o.id === organisation.id)
        ? this.state.organisations.map((o) => (o.id === organisation.id ? organisation : o))
        : [...this.state.organisations, organisation],
      orgZones,
      orgMembers,
      // Remembered past this screen too (s185 review): a zone opened from this
      // list keeps its name when the Office moves to another organisation.
      zoneRows: { ...this.state.zoneRows, ...Object.fromEntries(orgZones.map((z) => [z.id, z])) },
    });
  }

  rememberZoneRow(chat: Chat): void {
    this.set({ zoneRows: { ...this.state.zoneRows, [chat.id]: chat } });
  }

  setOrgBusy(orgBusy: boolean, orgError: string | null = null): void {
    this.set({ orgBusy, orgError });
  }

  setOrgForm(patch: Partial<ShellState["orgForm"]>): void {
    this.set({ orgForm: { ...this.state.orgForm, ...patch } });
  }

  setZoneForm(patch: Partial<ShellState["zoneForm"]>): void {
    this.set({ zoneForm: { ...this.state.zoneForm, ...patch } });
  }

  /** This user is no longer in that organisation — by leaving, or by being
   *  removed on another device. Everything about it goes. */
  forgetOrganisation(orgId: string): void {
    const gone = this.state.activeOrgId === orgId;
    this.set({
      organisations: this.state.organisations.filter((o) => o.id !== orgId),
      activeOrgId: gone ? null : this.state.activeOrgId,
      orgZones: gone ? [] : this.state.orgZones,
      orgMembers: gone ? [] : this.state.orgMembers,
      zoneRows: Object.fromEntries(Object.entries(this.state.zoneRows).filter(([, z]) => z.org_id !== orgId)),
      screen: gone ? "office_orgs" : this.state.screen,
    });
  }

  setPublishers(publishers: Publisher[]): void {
    this.set({ publishers, publishersLoaded: true });
  }

  /** Written from the SERVER's answer, never optimistically: s136 paid for the
   *  alternative on Explore's follow button, where the control flipped and the
   *  request had failed. */
  applyPublishing(publisherId: string, publishes: boolean): void {
    this.set({
      publishers: this.state.publishers.map((p) =>
        p.id === publisherId ? { ...p, publishes } : p
      ),
    });
  }

  setOfficeTimeline(officeTimeline: ChannelPost[]): void {
    this.set({ officeTimeline });
  }

  setOfficeDraft(officeDraft: string): void {
    this.set({ officeDraft });
  }

  setOfficeBusy(officeBusy: boolean, officeError: string | null = null): void {
    this.set({ officeBusy, officeError });
  }

  setOfficeForm(patch: Partial<ShellState["officeForm"]>): void {
    const next = { ...this.state.officeForm, ...patch };
    // The industry only means something for a BUSINESS (A15, and a CHECK
    // enforces it). Clearing it here rather than in the view is what stops a
    // ministry carrying "retail" because somebody tapped Business first.
    if (patch.kind !== undefined && patch.kind !== "business") next.category = null;
    this.set({ officeForm: next, officeError: null });
  }

  setBusinessCategories(businessCategories: string[]): void {
    this.set({ businessCategories });
  }

  setExploreError(exploreError: string | null): void {
    this.set({ exploreError, exploreBusy: false });
  }

  /**
   * Flip one directory row to "following".
   *
   * Not optimism about the network — the button describes a relationship the
   * server has already been told about, and a failed request only means the
   * next open shows it unfollowed again.
   */
  markExploreFollowing(channelId: string): void {
    this.set({
      exploreResults: this.state.exploreResults.map((c) =>
        c.id === channelId ? { ...c, following: true } : c
      ),
    });
  }

  /**
   * The publisher kinds from the server.
   *
   * The `?? []` is not defensive decoration: `exploreView` reads
   * `publisherKinds.length` while building, so an `undefined` here is not a
   * missing facet row — it is a THROW inside the render, and a throw inside
   * the render is a blank page. One of the two call sites already coerced and
   * the other did not, which is the shape of a bug waiting for a server that
   * answers 200 with a body nobody expected. Coercing HERE means neither call
   * site can get it wrong. Found by driving the browser against a mock that
   * returned a bare array; the screen went white and the state said `explore`.
   */
  setPublisherKinds(publisherKinds: string[] | null | undefined): void {
    this.set({ publisherKinds: publisherKinds ?? [] });
  }

  /**
   * Clear a channel's badge locally.
   *
   * Not an optimism about the network: the badge is a fact about THIS READER,
   * so showing it read the instant they opened it IS the truth, and a failed
   * request only means the next refresh restores the count.
   */
  clearChannelUnread(channelId: string): void {
    this.set({
      channels: this.state.channels.map((c) => (c.id === channelId ? { ...c, unread: 0 } : c)),
    });
  }

  openChannel(channelId: string): void {
    this.set({
      channelViewing: channelId, channelDetail: null, channelPosts: [], channelBusy: true, channelError: null,
      channelAtEnd: false, channelLoadingMore: false, channelPlaying: [], channelLightbox: null,
      channelForwarding: null, channelPicker: null, channelNotice: null,
    });
  }

  /** The first page. `atEnd` when the server answered fewer than it pages
   *  by, so "load older" is never offered for a channel with nothing older. */
  setChannelPage(detail: Channel | null, posts: ChannelPost[], atEnd: boolean): void {
    this.set({ channelDetail: detail, channelPosts: posts, channelBusy: false, channelError: null, channelAtEnd: atEnd });
  }

  /** Kept for the callers that only have posts (the Office timeline read). */
  setChannelPosts(posts: ChannelPost[]): void {
    this.set({ channelPosts: posts, channelBusy: false, channelError: null });
  }

  setChannelLoadingMore(loading: boolean): void {
    this.set({ channelLoadingMore: loading });
  }

  /** Older posts, appended after what is held. A post already held is not
   *  added twice: a page boundary can serve the cursor post again. */
  appendChannelPosts(older: ChannelPost[], atEnd: boolean): void {
    const held = new Set(this.state.channelPosts.map((p) => p.id));
    const fresh = older.filter((p) => !held.has(p.id));
    this.set({ channelPosts: [...this.state.channelPosts, ...fresh], channelLoadingMore: false, channelAtEnd: atEnd });
  }

  setChannelError(message: string | null): void {
    this.set({ channelError: message, channelBusy: false, channelLoadingMore: false });
  }

  /** One post's aggregate from the SERVER's reply — never a local +1: the
   *  count is `COUNT(*)` over readers, and this browser cannot know it. */
  setChannelReaction(postId: string, wire: { reactions?: ReactionCount[]; reaction_total?: number; my_reaction?: string | null }): void {
    this.set({
      channelPicker: null,
      channelPosts: this.state.channelPosts.map((p) =>
        p.id === postId
          ? { ...p, reactions: wire.reactions ?? [], reaction_total: wire.reaction_total ?? 0, my_reaction: wire.my_reaction ?? null }
          : p
      ),
    });
  }

  /** Mute lives on the detail AND on the list row, which is where the badge
   *  reads it; both flip together so the two never disagree. */
  setChannelMuted(channelId: string, muted: boolean): void {
    this.set({
      channelDetail: this.state.channelDetail && this.state.channelDetail.id === channelId
        ? { ...this.state.channelDetail, muted }
        : this.state.channelDetail,
      channels: this.state.channels.map((c) => (c.id === channelId ? { ...c, muted } : c)),
    });
  }

  playChannelPost(postId: string): void {
    if (this.state.channelPlaying.includes(postId)) return;
    this.set({ channelPlaying: [...this.state.channelPlaying, postId] });
  }

  setChannelLightbox(postId: string | null): void {
    this.set({ channelLightbox: postId });
  }

  setChannelForwarding(postId: string | null): void {
    this.set({ channelForwarding: postId });
  }

  setChannelPicker(postId: string | null): void {
    this.set({ channelPicker: postId });
  }

  setChannelNotice(channelNotice: string | null): void {
    this.set({ channelNotice });
  }

  closeChannel(): void {
    this.set({
      channelViewing: null, channelDetail: null, channelPosts: [], channelBusy: false, channelError: null,
      channelAtEnd: false, channelLoadingMore: false, channelPlaying: [], channelLightbox: null,
      channelForwarding: null, channelPicker: null, channelNotice: null,
    });
  }

  setPostBusy(busy: boolean, error: string | null = null): void {
    this.set({ postBusy: busy, feedError: error });
  }

  /** Open one post's comment box, closing whichever was open. Only one at a
   *  time: a feed with six open comment boxes is a feed nobody can read. */
  setCommenting(postId: string | null): void {
    this.set({ feedCommenting: postId, feedDraft: "" });
  }

  setCommentDraft(feedDraft: string): void {
    this.set({ feedDraft });
  }

  /** The viewer's OWN like, straight away — private and uncounted, so there is
   *  no total to keep in step. */
  setPostLiked(postId: string, liked: boolean): void {
    this.set({
      feed: this.state.feed.map((p) => (p.id === postId ? { ...p, liked } : p)),
    });
  }

  setPostComments(postId: string, comments: PostComment[]): void {
    this.set({
      feed: this.state.feed.map((p) => (p.id === postId ? { ...p, comments } : p)),
    });
  }

  dropPost(postId: string): void {
    this.set({ feed: this.state.feed.filter((p) => p.id !== postId) });
  }

  /** A blob landed; the pure view builder needs a reason to rebuild. */
  bumpStatusTick(): void {
    this.set({ statusTick: this.state.statusTick + 1 });
  }

  /**
   * Mark an item seen locally, straight away.
   *
   * The ring has to stop glowing the instant it is opened; the server call is
   * what makes it true on the user's other devices. Being wrong in the other
   * direction — claiming unseen — would make the ring lie about something the
   * user has just done.
   */
  markStatusViewed(statusId: string): void {
    this.set({
      statusTray: this.state.statusTray.map((entry) => {
        if (!entry.items.some((i) => i.id === statusId)) return entry;
        const items = entry.items.map((i) => (i.id === statusId ? { ...i, viewed: true } : i));
        return { ...entry, items, all_viewed: items.every((i) => i.viewed) };
      }),
    });
  }

  setStatusLiked(statusId: string, liked: boolean): void {
    this.set({
      statusTray: this.state.statusTray.map((entry) => ({
        ...entry,
        items: entry.items.map((i) => (i.id === statusId ? { ...i, liked } : i)),
      })),
    });
  }

  /**
   * The signed-in user, as last read.
   *
   * ADR-0028: the account owns the interface language, so learning who we are
   * is also learning which language to draw. Adopted here rather than at each
   * call site, because two call sites are two chances to forget one — and the
   * one that forgets is a device that quietly keeps drawing English after the
   * person chose Kiswahili on their phone.
   */
  setProfile(profile: User | null): void {
    this.set({ profile });
  }

  setProfileBusy(profileBusy: boolean): void {
    this.set({ profileBusy });
  }

  setUiLanguage(uiLanguage: string | null): void {
    this.set({ uiLanguage });
  }

  /** Adopt the ACCOUNT's language, which is the owner of this setting (D3).
   *  A null there means the account has never chosen, and the browser decides. */
  adoptAccountLanguage(profile: User | null): void {
    if (!profile) return;
    this.set({ uiLanguage: decodeLanguage(profile.ui_language) });
    try {
      if (profile.ui_language) localStorage.setItem(LANGUAGE_KEY, profile.ui_language);
      else localStorage.removeItem(LANGUAGE_KEY);
    } catch {
      // Storage blocked; the value still applies for this session.
    }
  }

  setProfileError(profileError: string | null): void {
    this.set({ profileError });
  }

  setPrivacy(privacy: ShellState["privacy"]): void {
    this.set({ privacy });
  }

  setFindability(findability: ShellState["findability"]): void {
    this.set({ findability });
  }

  setHistory(history: ShellState["history"]): void {
    this.set({ history });
  }

  /** An avatar blob landed — repaint whatever is showing. */
  bumpAvatars(): void {
    this.set({ avatarTick: this.current.avatarTick + 1 });
  }

  upsertChat(chat: Chat): void {
    const chats = this.current.chats.slice();
    const i = chats.findIndex((c) => c.id === chat.id);
    if (i >= 0) chats[i] = { ...chats[i], ...chat };
    else chats.unshift(chat);
    this.set({ chats });
  }

  setChats(chats: Chat[]): void {
    this.set({ chats });
  }

  openChat(chatId: string): void {
    this.set({
      screen: "conversation",
      activeChatId: chatId,
      messages: [],
      // Kept when the SAME chat is reopened (a second click on its row): the
      // shell's asked-about set survives that, so clearing the names would
      // leave the ninth voice "Unknown" with nothing left to ask (s175 review).
      rosterNames: chatId === this.current.activeChatId ? this.current.rosterNames : {},
      draft: "",
      typing: [],
      error: null,
      // Cleared, not kept: the last chat's answer on this chat's header is a
      // lock drawn over a conversation that has none.
      activeChatE2E: null,
      // A zone's refusal is about THAT zone; the next chat opens unrefused
      // until its own server answer says otherwise.
      activeZoneUpdateRequired: false,
      // Same reason, louder: a safety number is a claim about ONE pair of
      // people, and leaving it up over a different conversation would be the
      // worst sentence this surface can say.
      safetyNumber: null,
    });
  }

  /** ADR-0007 — what the server said about the OPEN chat. Ignored when the
   *  chat has since changed, so a slow answer cannot label the next one.
   *
   *  It writes the LIST row as well as the header. The two draw the same fact
   *  from two fields, and this is the fresher of them: `GET /chats` answered
   *  once, `GET /e2e/chats/:id/state` answers on every open. A chat sealed
   *  since the last list refresh would otherwise wear a lock in its header and
   *  none on the row directly behind it. */
  setChatE2E(chatId: string, e2e: boolean): void {
    const chats = this.current.chats.map((c) => (c.id === chatId ? { ...c, e2e } : c));
    if (this.current.activeChatId !== chatId) {
      this.set({ chats });
      return;
    }
    this.set({ chats, activeChatE2E: e2e });
  }

  /**
   * Back OUT of a conversation — it is closed, and its messages go with it.
   *
   * The narrow layout's Back, and logout. NOT the rail's Chats slot: see
   * `showChats`.
   */
  backToChats(): void {
    this.set({
      screen: "chats", activeChatId: null, messages: [], typing: [],
      activeChatE2E: null, safetyNumber: null, activeZoneUpdateRequired: false,
    });
  }

  /** ADR-0037 B — what the server said about the open zone's floor. Ignored
   *  when the chat has since changed, like [setChatE2E]. */
  setZoneUpdateRequired(chatId: string, refused: boolean): void {
    if (this.current.activeChatId !== chatId) return;
    if (this.current.activeZoneUpdateRequired === refused) return;
    this.set({ activeZoneUpdateRequired: refused });
  }

  /**
   * Show the chat LIST, leaving whatever is in the detail pane alone.
   *
   * The rail's Chats slot. Tapping Updates keeps the open thread beside the
   * new list, and Chats has to behave the same way or the one slot that means
   * "the conversations" is the one that closes your conversation. It also
   * matters below the surface: `backToChats` here would clear `activeChatId`
   * while the socket stayed subscribed to that chat's topic, because the rail
   * deliberately does not leave the channel.
   */
  showChats(): void {
    this.set({ screen: "chats" });
  }

  /** Locally zero a chat's unread badge (after marking read). */
  markChatReadLocal(chatId: string): void {
    this.set({
      chats: this.current.chats.map((c) => (c.id === chatId ? { ...c, unread_count: 0 } : c)),
    });
  }

  /** s175: names from the roster, for the chat that asked — a late answer for
   *  a chat already closed is dropped, not drawn on the next one. */
  addRosterNames(chatId: string, names: Record<string, string>): void {
    if (this.current.activeChatId !== chatId) return;
    this.set({ rosterNames: { ...this.current.rosterNames, ...names } });
  }

  // --- messages ------------------------------------------------------------

  /** Replace the active chat's message list (e.g. from history), oldest-first. */
  setMessages(messages: Message[]): void {
    this.set({ messages: sortByTime(messages) });
  }

  /**
   * Fold a batch of messages into the active chat, keeping whatever is already
   * there.
   *
   * History is fetched asynchronously and a person can type before it lands.
   * `setMessages` REPLACES, so a slow history response arriving after an
   * optimistic send silently deletes the bubble the sender is looking at — the
   * message is on its way to the server and gone from the screen. That is not a
   * hypothetical: ADR-0027 D4.6 added one more await to the open-chat path and
   * the browser E2E caught it immediately, which is what a real browser is for.
   *
   * Same dedup as {@link upsertMessage} — by server id, then by
   * `client_msg_id` — so a history row and the echo it is the persisted form of
   * collapse into one.
   */
  mergeMessages(messages: Message[]): void {
    const list = this.current.messages.slice();
    for (const msg of messages) {
      const byId = msg.id ? list.findIndex((m) => m.id === msg.id) : -1;
      const byClient =
        msg.client_msg_id != null
          ? list.findIndex((m) => m.client_msg_id != null && m.client_msg_id === msg.client_msg_id)
          : -1;
      const idx = byId >= 0 ? byId : byClient;
      if (idx >= 0) list[idx] = { ...list[idx], ...msg, pending: msg.pending ?? false };
      else list.push(msg);
    }
    this.set({ messages: sortByTime(list) });
  }

  /**
   * Insert or update one message. Dedups so the optimistic local echo, the REST
   * response, and the channel broadcast for the SAME send collapse into one row:
   *   - same server id → update in place,
   *   - same client_msg_id as a pending echo → replace the echo (now persisted),
   *   - otherwise append (kept time-sorted).
   * Only touches state when the active chat matches.
   */
  upsertMessage(msg: Message): void {
    if (msg.chat_id !== this.current.activeChatId) {
      this.bumpChatPreview(msg);
      return;
    }
    const list = this.current.messages.slice();

    const byId = msg.id ? list.findIndex((m) => m.id === msg.id) : -1;
    const byClient =
      msg.client_msg_id != null
        ? list.findIndex((m) => m.client_msg_id != null && m.client_msg_id === msg.client_msg_id)
        : -1;
    const idx = byId >= 0 ? byId : byClient;

    if (idx >= 0) {
      list[idx] = { ...list[idx], ...msg, pending: msg.pending ?? false };
    } else {
      list.push(msg);
    }
    this.set({ messages: sortByTime(list) });
    this.bumpChatPreview(msg);
  }

  /**
   * Drop one optimistic local echo, by the client id that created it.
   *
   * For a send the server has REFUSED for good — ADR-0027 D2d's encrypted-chat
   * refusal is the first — where leaving the bubble on screen is the lie.
   * There is no retry on this surface and no failed state to render, so a
   * `pending: true` row that will never be acked sits there looking like a
   * message on its way, forever. Removing it is the only truthful option the
   * renderer currently has.
   *
   * Deliberately keyed on `client_msg_id` and not `id`: the echo has no server
   * id, and the client id is the only handle that survives from `doSend`.
   */
  removeMessage(clientMsgId: string): void {
    const list = this.current.messages.filter((m) => m.client_msg_id !== clientMsgId);
    if (list.length !== this.current.messages.length) this.set({ messages: list });
  }

  setDraft(draft: string): void {
    this.set({ draft });
  }

  clearDraft(): void {
    this.set({ draft: "" });
  }

  // --- mini-app picker -----------------------------------------------------

  /** Open the picker screen on the Featured tab (and mark the list loading). */
  openApps(): void {
    this.set({ screen: "apps", appsTab: "featured", appsQuery: "", appsLoading: true, appsError: null });
  }

  /** Switch tab — clears any search and marks the new list loading. */
  setAppsTab(tab: AppsTab): void {
    this.set({ appsTab: tab, appsQuery: "", appsLoading: true, appsError: null });
  }

  /** Reflect the typed query immediately (no network) — the debounced search
   *  fires the fetch separately. Hides the tab bar (`appsQuery` ≠ "" ⇒ searching). */
  setAppsQuery(query: string): void {
    this.set({ appsQuery: query });
  }

  /** Begin a search for `query` (the current tab is preserved). */
  beginAppsSearch(query: string): void {
    this.set({ appsQuery: query, appsLoading: true, appsError: null });
  }

  /** Clear the search box and reload the current tab. */
  clearAppsSearch(): void {
    this.set({ appsQuery: "", appsLoading: true, appsError: null });
  }

  setApps(apps: MiniAppListItem[]): void {
    this.set({ apps, appsLoading: false, appsError: null });
  }

  setAppsError(error: string): void {
    this.set({ appsLoading: false, appsError: error });
  }

  // --- embedded mini-app ---------------------------------------------------

  openMiniApp(title: string): void {
    this.set({ miniApp: { open: true, title, status: "loading", error: null } });
  }

  setMiniAppStatus(status: MiniAppStatus, error: string | null = null): void {
    this.set({ miniApp: { ...this.current.miniApp, status, error } });
  }

  closeMiniApp(): void {
    this.set({ miniApp: { open: false, title: "", status: "idle", error: null } });
  }

  // --- typing presence -----------------------------------------------------

  setTyping(userId: string, isTyping: boolean): void {
    const has = this.current.typing.includes(userId);
    if (isTyping && !has) this.set({ typing: [...this.current.typing, userId] });
    else if (!isTyping && has) this.set({ typing: this.current.typing.filter((u) => u !== userId) });
  }

  // --- internals -----------------------------------------------------------

  // Reflect a new/updated message into the chat-list preview + unread badge so
  // the list stays live even when the chat isn't open.
  private bumpChatPreview(msg: Message): void {
    const chats = this.current.chats.map((c) => {
      if (c.id !== msg.chat_id) return c;
      const isActive = this.current.activeChatId === c.id;
      const mine = this.current.me?.id != null && msg.user_id === this.current.me.id;
      return {
        ...c,
        last_message: {
          id: msg.id,
          body: msg.body,
          user_id: msg.user_id,
          message_type: msg.message_type,
          created_at: msg.created_at,
          // Carried, not dropped: the body is EMPTY for every media message,
          // so a preview rebuilt from the text alone would turn a live photo
          // or voice note back into "No messages yet" the moment it arrived —
          // undoing the server's own answer.
          status: msg.status ?? null,
          voice: (msg.metadata?.voice as boolean | undefined) ?? false,
          duration_ms: (msg.metadata?.duration_ms as number | undefined) ?? null,
        },
        unread_count: isActive || mine ? c.unread_count : c.unread_count + 1,
      };
    });
    this.set({ chats });
  }
}

function sortByTime(messages: Message[]): Message[] {
  return messages.slice().sort((a, b) => {
    const ta = a.created_at ?? "";
    const tb = b.created_at ?? "";
    if (ta === tb) return 0;
    return ta < tb ? -1 : 1;
  });
}

/**
 * A conversation's row wherever this browser holds it: the Chats list, the
 * open organisation's zones, or a zone fetched on its own. A zone is never in
 * `chats`, so a lookup there alone named every zone "New conversation".
 */
export function chatById(state: ShellState, id: string | null): Chat | undefined {
  if (!id) return undefined;
  return state.chats.find((c) => c.id === id)
    ?? state.orgZones.find((c) => c.id === id)
    ?? state.zoneRows[id];
}
